import {Editor} from '@tiptap/react';
import React, {FC, useState} from 'react';

import {InputDialog} from '../../../../../../../src/components/common/dialogs/inputDialog';
import {sanitizeHrefInput} from '../../../../../../shared/helpers/src/browser/urlHelpers';
import {Trans} from '../../../../../../shared/helpers/src/internationalization/react';
import {ToolbarButton} from '../../../../../../shared/ui/src/components/buttons/toolbarButton';
import {useUserTheme} from '../../../../../../shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

interface LinkButtonProps {
  editor: Editor;
}

/*
 * Component.
 */

export const LinkButton: FC<LinkButtonProps> = ({editor}) => {
  const theme = useUserTheme();
  const [isShowingUrlDialog, showUrlDialog] = useState(false);
  const selectedUrl = editor.getAttributes('link').href;

  const setLink = (value: string) => {
    // returns empty string if invalid
    const url = sanitizeHrefInput(value);

    // clear link
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      showUrlDialog(false);
      return;
    }

    if (editor.view.state.selection.empty) {
      editor.chain().focus().setLink({href: url}).insertContent(url).run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({href: url}).run();
    }

    showUrlDialog(false);
  };

  return (
    <>
      {isShowingUrlDialog && (
        <InputDialog
          title={renderTitle(!selectedUrl)}
          initialValue={(selectedUrl && sanitizeHrefInput(selectedUrl)) || ''}
          onValueSelect={setLink}
          onRequestClose={() => showUrlDialog(false)}
        />
      )}

      <ToolbarButton
        iconName="formattingLink"
        isActive={editor.isActive('link')}
        onClick={() => showUrlDialog(true)}
        label={<Trans id="knowledge-base-formatting-link">Insert link</Trans>}
        shouldPreventMouseDown
        color={theme.greys.shade80}
        hoverColor={theme.greys.shade80}
      />
    </>
  );
};

function renderTitle(isInserting: boolean) {
  if (isInserting) {
    return <Trans id="knowledge-base-link-dialog-title-insert">Insert a link</Trans>;
  }

  return <Trans id="knowledge-base-link-dialog-title-update">Update a link</Trans>;
}
